import Dropdown from './Dropdown';
import { ComboboxList } from './ComboboxList';
import {XMarkIcon} from "@heroicons/react/24/outline";

type FormDropdownOption = {
  value: string | number;
  label: string;
  searchAliases?: string[];
};

type FormDropdownProps = {
  value?: string | number ;
  onChange?: (value: string | number ) => void;
  options: FormDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  showClearButton?: boolean;
  searchable?: boolean;
  anchor?: false | 'bottom' | 'top';
  menuWidth?: string;
  openMenuOnFocus?: boolean;
  classNames?: {
    input?: string;
    options?: string;
  };
  'aria-label'?: string;
  'aria-labelledby'?: string;
};

export function FormDropdown({
  value,
  onChange,
  options,
  placeholder = 'Selecteer...',
  disabled = false,
  showClearButton = false,
  searchable = false,
  anchor = 'bottom',
  menuWidth = 'w-full',
  openMenuOnFocus = false,
  classNames,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: FormDropdownProps) {
  const selectedOption = options.find(opt => opt.value === value);
  const hasValue = selectedOption !== undefined;
  const resolvedInputClassName = classNames?.input;
  const resolvedOptionsClassName = classNames?.options;

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange) {
      onChange('');
    }
  };

  if (searchable) {
    return (
      <ComboboxList<FormDropdownOption>
        anchor={anchor}
        menuWidth={menuWidth}
        openMenuOnFocus={openMenuOnFocus}
        comboBoxClassName={resolvedInputClassName}
        menuClassName={resolvedOptionsClassName}
        emptySelectionMessage={placeholder}
        options={options}
        selected={selectedOption || null}
        onSelect={(option) => {
          if (onChange) {
            onChange(option ? option.value : '');
          }
        }}
        internalMapper={(item) => ({
          ...item,
          displayName: item.label,
          id: String(item.value)
        })}
        disabled={disabled}
        showClearButton={showClearButton}
        ariaLabel={ariaLabel}
        ariaLabelledBy={ariaLabelledBy}
      />
    );
  }

  return (
    <Dropdown
      selected={{ 
        value: value ?? '', 
        label: selectedOption?.label || placeholder 
      }}
      options={options.map(opt => ({ 
        value: opt.value, 
        label: opt.label 
      }))}
      onUpdateFunc={(selectedValue) => {
        if (onChange) {
          onChange(selectedValue);
        }
      }}
      disabled={disabled}
      ariaLabel={ariaLabel}
      ariaLabelledBy={ariaLabelledBy}
      rightAccessory={
        showClearButton && hasValue && !disabled ? (
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center justify-center rounded-md hover:bg-gray-100 p-1"
            aria-label="Selectie wissen"
          >
            <XMarkIcon className="h-4 w-4 text-gray-400" aria-hidden="true"/>
          </button>
        ) : undefined
      }
    />
  );
}

export default FormDropdown;

